import { getActivePlatform } from "@/lib/platform/config";
import { resetRequestAcquisition } from "./request-acquisition";
import { getConsent, hasChosen, subscribeConsent } from "./consent";
const BASE = (import.meta.env.BASE_URL || "/").replace(/\/$/, "");
let token: string | null = null;
let queue: Promise<void> = Promise.resolve();
let lastActivity = 0;
let started = false;
let documentCaptured = false;
const enabled = () => getActivePlatform() === "bac" && hasChosen() && getConsent().analytics;
export function acquisitionConsent(): boolean | undefined {
  return getActivePlatform() === "bac" ? enabled() : undefined;
}
async function post(path: string, body: Record<string, unknown>): Promise<void> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 2500);
  try {
    await fetch(`${BASE}/api/acquisition/${path}`, {
      method: "POST", credentials: "same-origin", signal: controller.signal,
      headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify(body),
    });
  } catch { /* Optional analytics must never block auth/booking. */ }
  finally { clearTimeout(timer); }
}
export function refreshAcquisition(): Promise<void> {
  queue = queue.catch(() => {}).then(async () => {
    if (getActivePlatform() !== "bac") return;
    if (!enabled()) { await post("session", { analyticsConsent: false }); return; }
    const consent = getConsent();
    // Read only after consent: no pre-consent URL/referrer memory buffer.
    const documentEntry = !documentCaptured;
    documentCaptured = true;
    await post("session", {
      documentEntry,
      surface: "web", url: window.location.href, referrer: document.referrer,
      analyticsConsent: true, marketingConsent: consent.marketing,
    });
    if (!enabled()) { await post("session", { analyticsConsent: false }); return; }
    if (token) await post("bind", { acquisitionConsent: true });
  });
  return queue;
}
export function bindAcquisition(accessToken: string | null): void {
  const logout = !!token && !accessToken;
  token = accessToken;
  if (logout && getActivePlatform() === "bac") {
    resetRequestAcquisition();
    queue = queue.then(() => post("session", { analyticsConsent: false }));
    return;
  }
  if (enabled()) void refreshAcquisition();
}
export function initAcquisition(): void {
  if (started || typeof window === "undefined" || getActivePlatform() !== "bac") return;
  started = true;
  subscribeConsent(() => { void refreshAcquisition(); });
  // Default/pending has no collection request. Explicit denial only removes state.
  if (enabled()) void refreshAcquisition();
  window.addEventListener("pointerdown", () => {
    if (!enabled() || Date.now() - lastActivity < 60_000) return;
    lastActivity = Date.now();
    void refreshAcquisition();
  }, { passive: true });
}
